/**
 * L'attestation d'information au conjoint commun en biens.
 *
 * Contrairement à la déclaration de non-condamnation, dont l'administration ne
 * publie qu'un modèle de rédaction, celle-ci a un modèle FIXÉ PAR ARRÊTÉ :
 * l'arrêté du 4 juillet 2007 (NOR JUSC0759543A), pris pour l'application de
 * l'article R. 123-121-1 du code de commerce, qui exige une attestation
 * « établie conformément à un modèle défini par arrêté ». La phrase ci-dessous
 * est donc recopiée au mot près, et ce qui s'en écarte se justifie ici ou
 * nulle part.
 *
 * CE QUE LE MODÈLE LAISSE À TRANCHER, et que nous tranchons à la place du
 * lecteur plutôt que de lui laisser des mentions à rayer :
 *   1. « mon conjoint M./Mme (rayer la mention inutile) » : la civilité du
 *      conjoint choisit, et l'autre disparaît.
 *   2. « avec lequel/laquelle » : même chose.
 *   3. « je me suis marié(e) » : la civilité du déclarant accorde, la maison
 *      n'écrivant pas en inclusif.
 *   4. « sans contrat de mariage (1) ou bien avec un contrat de mariage qui
 *      prévoit des biens communs aux époux » : deux branches exclusives, une
 *      seule est écrite. La note (1) ne suit que la première, qui est la seule
 *      qu'elle éclaire.
 *   5. « (Signature de la personne immatriculée) » n'est pas écrit : c'est une
 *      consigne de gabarit, pas une phrase de lettre. Le nom du déclarant la
 *      remplace sous le paraphe (décidé le 07/09/2026).
 *
 * NE PAS CONFONDRE AVEC L'AUTRE DOCUMENT DU CONJOINT. Celui-ci est signé par
 * l'entrepreneur, atteste qu'il a informé son conjoint des conséquences de ses
 * dettes professionnelles sur les biens communs, et n'est exigé que d'un
 * entrepreneur marié sous un régime de communauté. L'attestation sur l'honneur
 * du conjoint qui choisit son statut (collaborateur, salarié, associé) est un
 * document distinct, signé par le conjoint, et qui ne concerne que les couples
 * dont le conjoint travaille dans l'entreprise, mariés, pacsés ou concubins.
 */

import {
    dateEnToutesLettres,
    identite,
    nettoyer,
} from "./documentText.js";

/** L'article dont l'attestation est prise, et le seul cité dans le document. */
export const ARTICLE = "l'article R. 123-121-1 du code de commerce";

/** Le renvoi de bas de page du modèle, mot pour mot. */
export const NOTE =
    "(1) La mention relative à l'absence de contrat de mariage ne signifie pas " +
    "que le régime légal français est applicable.";

/**
 * Qui doit ce document, et qui ne le doit pas.
 *
 * Le tri se fait avant toute saisie : demander son état civil à quelqu'un qui
 * repartira sans document serait une perte de temps déguisée en formulaire.
 * `motif` nomme la raison du refus, jamais la phrase : les libellés sont chez
 * l'appelant, qui les a dans les deux langues.
 */
export function concerne(reponses) {
    const r = reponses || {};
    if (r.situation && r.situation !== "marie") {
        return { concerne: false, motif: r.situation === "pacse" ? "pacse" : "seul" };
    }
    if (r.regime === "separation") {
        return { concerne: false, motif: "separation" };
    }
    if (!r.situation || !r.regime) return { concerne: null, motif: null };
    return { concerne: true, motif: null };
}

/** Ce qui manque encore. Les clés nomment des champs, pas des phrases. */
export function valider(reponses) {
    const r = reponses || {};
    const manques = [];

    const invalides = [];
    if (!concerne(r).concerne) return { valide: false, manques: ["situation"], invalides };

    const requis = [
        // La civilité accorde « Je soussignée » et « mariée » : sans réponse, le
        // document parlerait au masculin de quelqu'un qui n'a rien choisi.
        ["civilite", r.civilite],
        ["prenoms", r.prenoms],
        ["nom", r.nom],
        ["conjointCivilite", r.conjointCivilite],
        ["conjointPrenoms", r.conjointPrenoms],
        ["conjointNom", r.conjointNom],
        ["lieuDeSignature", r.lieuDeSignature],
    ];
    for (const [cle, valeur] of requis) {
        if (!nettoyer(valeur)) manques.push(cle);
    }

    const dateBrute = nettoyer(r.dateDeSignature);
    if (!dateBrute) manques.push("dateDeSignature");
    else if (!dateEnToutesLettres(dateBrute)) {
        invalides.push({ champ: "dateDeSignature", motif: "format" });
    }

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques,
        invalides,
    };
}

/**
 * Le document, en blocs, prêt à être posé sur une page ou dans un PDF.
 *
 * Rend `null` tant qu'il manque une réponse : un document à trous ne se
 * télécharge pas, et l'appelant n'a pas à décider ce qu'il fait d'un trou.
 */
export function composerAttestation(reponses, aujourdhui) {
    const r = reponses || {};
    const dateDeSignature = nettoyer(r.dateDeSignature) || String(aujourdhui).slice(0, 10);
    const complet = { ...r, dateDeSignature };
    if (!valider(complet).valide) return null;

    const declaranteFeminin = r.civilite === "madame";
    const conjointeFeminin = r.conjointCivilite === "madame";
    const sansContrat = r.regime === "sansContrat";

    const moi = identite({ prenoms: r.prenoms, nom: r.nom });
    const conjoint = identite({ prenoms: r.conjointPrenoms, nom: r.conjointNom });

    // Le régime s'écrit dans l'ordre du modèle : la branche retenue, et le
    // renvoi (1) qui n'éclaire que l'absence de contrat.
    const regime = sansContrat
        ? "sans contrat de mariage (1)"
        : "avec un contrat de mariage qui prévoit des biens communs aux époux";

    const phrase =
        `Je soussign${declaranteFeminin ? "ée" : "é"} ${moi} déclare sous ma responsabilité, ` +
        `conformément à ${ARTICLE}, avoir informé mon conjoint ` +
        `${conjointeFeminin ? "Mme" : "M."} ${conjoint}, avec ` +
        `${conjointeFeminin ? "laquelle" : "lequel"} je me suis ` +
        `mari${declaranteFeminin ? "ée" : "é"} ${regime}, sur les conséquences des dettes ` +
        "contractées dans l'exercice de ma profession sur ces biens communs.";

    return {
        titre:
            "Attestation de délivrance de l'information donnée à son conjoint commun " +
            "en biens sur les conséquences des dettes contractées dans l'exercice de sa " +
            "profession sur les biens communs",
        blocs: [
            { type: "paragraphe", texte: phrase },
            {
                type: "paragraphe",
                texte: `Fait à ${nettoyer(r.lieuDeSignature)}, le ${dateEnToutesLettres(dateDeSignature)}.`,
            },
            // L'arrêté écrit « (Signature de la personne immatriculée) » ; nous
            // ne portons pas cette consigne de gabarit dans la lettre. Le nom
            // du déclarant la remplace sous le paraphe, comme sur nos autres
            // documents : sans elle et sans lui, le bas de page serait anonyme.
            { type: "signature", nom: moi },
            // Le renvoi ne s'imprime que sous la branche qui le porte.
            ...(sansContrat ? [{ type: "note", texte: NOTE }] : []),
        ],
    };
}
